// src/app/nav.ts
import type { components } from "@/api/schema";

import type { PanelMode } from "./panel-mode";

type Role = components["schemas"]["UserRoleType"] | "ADMIN_ONLY" | "PLATFORM_ONLY";

export type NavItem = {
  id: string;
  label: string;
  n: string;
  dot: string;
  to: string;
  needsProject?: boolean;
  /** الأدوار المسموح لها برؤية العنصر (أدمن المنصة يرى كل شيء). */
  roles: Role[];
  /** بادئة المسار التي يُعتبر العنصر نشطاً تحتها (لو تختلف عن المسار نفسه). */
  match?: string;
};

const GOLD = "#D6AC55";

export const NAV: NavItem[] = [
  { id: "dashboard", label: "لوحة القيادة", n: "03", dot: "#8FC4F0", to: "/dashboard", roles: ["OWNER", "ACCOUNTANT", "BENEFICIARY", "TENANT", "SUPERVISOR"] },
  { id: "projects", label: "المشاريع والوحدات", n: "04", dot: "#8FC4F0", to: "/projects", roles: ["OWNER"] },
  { id: "units", label: "وحدات المشروع", n: "05", dot: "#6FD39A", to: "/projects/$projectId/units", needsProject: true, roles: ["OWNER"] },
  { id: "contracts", label: "العقود والدفعات", n: "08", dot: GOLD, to: "/projects/$projectId/contracts", needsProject: true, roles: ["OWNER"] },
  // بلا needsProject: أدمن المنصة (الجمهور الوحيد لده) ما عندوش "مشروع نشط" مُحدَّد أصلاً (لا دور له على
  // أي مشروع عميل)، فالرابط بيودّي لصفحة اختيار مشروع بدل رابط مُجهَّز بمعرّف مشروع مسبقاً.
  { id: "letters", label: "الخطابات", n: "10", dot: GOLD, to: "/letters", roles: ["PLATFORM_ONLY"] },
  { id: "journal", label: "دفتر اليومية", n: "12", dot: GOLD, to: "/accounting/journal-entries", roles: ["ACCOUNTANT", "OWNER"] },
  { id: "disbursements", label: "طلب صرف", n: "14", dot: "#E8C06A", to: "/accounting/disbursements", roles: ["ACCOUNTANT", "OWNER"] },
  { id: "waqf", label: "الأوقاف والمستفيدون", n: "16", dot: "#6FD39A", to: "/waqf", roles: ["OWNER", "BENEFICIARY"] },
  { id: "portal", label: "بوابة المستفيد", n: "20", dot: "#6FD39A", to: "/beneficiary-portal", roles: ["BENEFICIARY"] },
  { id: "maintenance", label: "الصيانة والبلاغات", n: "21", dot: "#F0736A", to: "/projects/$projectId/inspections", needsProject: true, roles: ["OWNER", "TENANT", "SUPERVISOR"] },
  { id: "vendor-work-orders", label: "أوامر العمل المكلَّف بها", n: "22", dot: "#F0736A", to: "/vendor/work-orders", roles: ["VENDOR"] },
  { id: "procurement", label: "المشتريات والموردون", n: "24", dot: "#8FC4F0", to: "/projects/$projectId/procurement", needsProject: true, roles: ["OWNER", "ACCOUNTANT"] },
  { id: "assets", label: "الأصول والعدادات", n: "29", dot: "#6FD39A", to: "/projects/$projectId/assets", needsProject: true, roles: ["OWNER"] },
  { id: "kpis", label: "مؤشرات الإدارة", n: "31", dot: GOLD, to: "/kpis", roles: ["OWNER"] },
  { id: "reports", label: "مركز التقارير", n: "33", dot: GOLD, to: "/reports", roles: ["OWNER", "ACCOUNTANT"] },
  { id: "platform", label: "إدارة المنصة", n: "40", dot: GOLD, to: "/platform", roles: ["PLATFORM_ONLY"] },
  { id: "platform-r", label: "موارد المنصة", n: "41", dot: "#8FC4F0", to: "/platform/r/organizations", match: "/platform/r", roles: ["PLATFORM_ONLY"] },
  { id: "platform-settings", label: "إعدادات المنصة", n: "42", dot: "#6FD39A", to: "/platform/settings", roles: ["PLATFORM_ONLY"] },
  { id: "platform-ai", label: "مزوّدو الذكاء الاصطناعي", n: "44", dot: "#8FC4F0", to: "/platform/ai-providers", roles: ["PLATFORM_ONLY"] },
  { id: "platform-ai-logs", label: "سجل نداءات الذكاء الاصطناعي", n: "47", dot: "#8FC4F0", to: "/platform/ai-call-logs", roles: ["PLATFORM_ONLY"] },
  { id: "platform-secrets", label: "الأسرار العامة", n: "45", dot: "#E8C06A", to: "/platform/secrets", roles: ["PLATFORM_ONLY"] },
  { id: "platform-logs", label: "سجل الإجراءات", n: "43", dot: "#E8C06A", to: "/platform/r/action-logs", roles: ["PLATFORM_ONLY"] },
  { id: "platform-leads", label: "العملاء المحتملون", n: "46", dot: "#F0736A", to: "/platform/r/leads", roles: ["PLATFORM_ONLY"] },
  { id: "admin", label: "الحوكمة والتكاملات", n: "32", dot: "#8FC4F0", to: "/admin/integrations", roles: ["ADMIN_ONLY"] },
  { id: "risks", label: "سجل المخاطر", n: "—", dot: "#F0736A", to: "/admin/risk-register", roles: ["ADMIN_ONLY"] },
];

/**
 * حساب عادي (مش أدمن منصة): تُفلتر عناصره بأدواره فقط، وتُخفى عناصر ADMIN_ONLY وPLATFORM_ONLY —
 * زي ما كان دايماً بالظبط.
 *
 * حساب أدمن منصة "هجين" (له مشاريعه الخاصة كمان، hybrid=true): يشوف لوحة واحدة بس في المرة —
 * عناصر المنصة/الحوكمة لو mode="platform"، أو عناصر مشاريعه العادية لو mode="projects" — والسويتش
 * الظاهر فوق (PanelSwitch) هو اللي بيبدّل بينهم بوضوح. حساب أدمن منصة "خالص" (مالوش مشاريع، مش
 * هجين) يشوف عناصر المنصة زي ما كان دايماً بلا أي سويتش.
 */
export function visibleNav(isPlatformAdmin: boolean, roles: string[], mode: PanelMode = "platform", hybrid = false): NavItem[] {
  return NAV.filter((i) => {
    const platformItem = i.roles.includes("ADMIN_ONLY") || i.roles.includes("PLATFORM_ONLY");
    if (platformItem) return isPlatformAdmin && (!hybrid || mode === "platform");
    if (!i.roles.some((r) => roles.includes(r))) return false;
    return !hybrid || mode === "projects";
  });
}
